import React, { useState } from "react";
import { toast } from "sonner";
import { Search, Download, Save, ExternalLink, Loader2, MessageCircleQuestion } from "lucide-react";
import { cloner, rawUrl } from "@/lib/clonerApi";
import { apiError } from "@/lib/userApi";

const KINDS = [["", "Todos"], ["pagina", "Páginas"], ["css", "CSS"], ["js", "JS"],
  ["imagen", "Imágenes"], ["fuente", "Fuentes"], ["media", "Medios"], ["otro", "Otros"]];
const TEXTY = ["pagina", "css", "js", "otro"];

const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

/** Archivos del sitio: buscar, ver, editar código y abrir páginas en el editor visual. */
export const FilesPanel = ({ siteId, files, onOpenPage }) => {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [open, setOpen] = useState(null);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const openFile = async (f) => {
    setOpen(f);
    setContent("");
    if (!TEXTY.includes(f.kind)) return;
    if (f.size > 150 * 1024 &&
        !window.confirm(`Este archivo pesa ${kb(f.size)} y puede tardar en abrirse. ¿Lo abro igual?`)) {
      return;
    }
    setBusy(true);
    try {
      const { data } = await cloner.text(siteId, f.path);
      setContent(data.content);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    try {
      await cloner.save(siteId, open.path, content);
      toast.success("Archivo guardado");
    } catch (e) { toast.error(apiError(e, "No se pudo guardar")); }
  };

  const list = files.filter((f) =>
    (!kind || f.kind === kind) &&
    (!q || f.path.toLowerCase().includes(q.toLowerCase())));

  if (open) {
    return (
      <div className="p-3" data-testid="file-viewer">
        <button onClick={() => setOpen(null)} className="text-[11px] text-white/50 mb-2"
          data-testid="file-viewer-back">← Volver a los archivos</button>
        <div className="text-[11px] text-white font-mono truncate mb-2">{open.path}</div>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-[#FF8A2B]" />}
        {open.kind === "imagen" && (
          <img src={rawUrl(siteId, open.path)} alt={open.name}
            className="max-w-full rounded-lg border border-white/10" />
        )}
        {open.kind === "media" && (
          <video src={rawUrl(siteId, open.path)} controls className="max-w-full rounded-lg" />
        )}
        {TEXTY.includes(open.kind) && !busy && (
          <>
            <textarea rows={18} value={content} onChange={(e) => setContent(e.target.value)}
              data-testid="file-code"
              className="w-full glass-card px-2 py-1.5 bg-black/50 text-white/85 text-[10px] font-mono outline-none" />
            <div className="flex items-center gap-1.5 mt-1.5">
              <button onClick={save} data-testid="file-save"
                className="glass-btn glass-btn-red px-3 py-1.5 rounded-lg text-[10px] flex items-center gap-1">
                <Save className="w-3 h-3" /> Guardar
              </button>
              {open.kind === "pagina" && (
                <button onClick={() => { onOpenPage(open.path); setOpen(null); }}
                  data-testid="file-open-visual"
                  className="glass-btn px-3 py-1.5 rounded-lg text-[10px] flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> Abrir en el editor
                </button>
              )}
            </div>
          </>
        )}
        <a href={rawUrl(siteId, open.path)} download data-testid="file-download"
          className="glass-btn px-3 py-1.5 rounded-lg text-[10px] mt-2 inline-flex items-center gap-1">
          <Download className="w-3 h-3" /> Descargar
        </a>
      </div>
    );
  }

  return (
    <div className="p-3" data-testid="files-panel">
      <div className="flex items-center gap-2 glass-card px-2 bg-black/40 mb-2">
        <Search className="w-3.5 h-3.5 text-white/40" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar archivo…"
          data-testid="files-search"
          className="flex-1 bg-transparent py-2 text-white text-[11px] outline-none" />
      </div>
      <div className="flex flex-wrap gap-1 mb-2">
        {KINDS.map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`files-kind-${k || "all"}`}
            className={`matrix-chip ${kind === k ? "is-active" : ""} px-2 py-0.5 rounded text-[10px]`}>
            {l}
          </button>
        ))}
      </div>
      <div className="divide-y divide-white/5">
        {list.slice(0, 500).map((f) => (
          <button key={f.path} onClick={() => openFile(f)} data-testid={`file-${f.path}`}
            className="w-full text-left px-1 py-1.5 flex items-center gap-2 hover:bg-white/[0.04]">
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/8 text-white/50 w-14 text-center shrink-0">
              {f.kind}
            </span>
            <span className="text-[11px] text-white/80 truncate flex-1">{f.path}</span>
            <span className="text-[10px] text-white/30 shrink-0">{kb(f.size)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/** Asistente de archivos: pregunta dónde está algo y te lleva al archivo. */
export const AskPanel = ({ siteId, onOpenPath }) => {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [chat, setChat] = useState([]);

  const ask = async () => {
    if (!q.trim()) return;
    const question = q;
    setQ("");
    setBusy(true);
    try {
      const { data } = await cloner.aiAsk(siteId, question);
      setChat((c) => [...c, { question, answer: data.answer, paths: data.paths }]);
    } catch (e) { toast.error(apiError(e, "La IA no pudo responder")); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-3" data-testid="ask-panel">
      <div className="flex items-center gap-2 glass-card px-2 bg-black/40">
        <MessageCircleQuestion className="w-3.5 h-3.5 text-[#FF8A2B]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="ask-input"
          onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="¿Dónde está el logo?"
          className="flex-1 bg-transparent py-2 text-white text-[11px] outline-none" />
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#FF8A2B]" />}
      </div>
      <div className="mt-3 space-y-3">
        {chat.map((c, i) => (
          <div key={i} data-testid={`ask-answer-${i}`}>
            <div className="text-[10px] text-white/40">{c.question}</div>
            <div className="text-[11px] text-white/80 mt-0.5">
              {c.answer.replace(/\*\*/g, "").replace(/`/g, "")}
            </div>
            {c.paths?.map((p) => (
              <button key={p} onClick={() => onOpenPath(p)} data-testid={`ask-path-${p}`}
                className="text-[10px] text-[#FF8A2B] font-mono block truncate mt-1">
                → {p}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
